import { PersonalCalendar } from "@/components/ai/personal-calendar";
import { Link } from 'react-router-dom';
import { Plus, Clock, Calendar, ChevronRight, Circle, CheckCircle2, AlertCircle, CheckSquare } from 'lucide-react';
import { format, isToday, isTomorrow, isPast, isThisWeek } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { useMyTasks } from '@/hooks/use-tasks';
import { useI18n } from '@/lib/i18n';
import { useAuthStore } from '@/stores/auth-store';
import { Button } from '@/components/ui/button';
import { useNotificationSubscription } from '@/hooks/use-notifications';
import { CreateProjectDialog } from '@/components/projects/create-project-dialog';
import { AppLayout } from '@/components/layout/app-layout';
import { useState } from 'react';
import { cn } from '@/lib/utils';

const statusConfig = {
    TODO: { label: 'To Do', icon: Circle, color: 'text-foreground-muted' },
    IN_PROGRESS: { label: 'In Progress', icon: Clock, color: 'text-blue-500' },
    IN_REVIEW: { label: 'In Review', icon: AlertCircle, color: 'text-amber-500' },
    DONE: { label: 'Done', icon: CheckCircle2, color: 'text-green-500' },
    CANCELLED: { label: 'Cancelled', icon: Circle, color: 'text-foreground-muted line-through' },
} as const;

export function DashboardPage() {
    const { user } = useAuthStore();
    const { t, language } = useI18n();
    const [showCreateDialog, setShowCreateDialog] = useState(false);

    // Subscribe to real-time notifications
    useNotificationSubscription();
    const { data: myTasksData } = useMyTasks({
        status: ['TODO', 'IN_PROGRESS', 'IN_REVIEW'],
        sortBy: 'dueDate',
        sortOrder: 'asc',
    });

    const tasks = myTasksData?.tasks || [];
    const stats = myTasksData?.stats || { assigned: 0, dueToday: 0, completedThisWeek: 0 };

    const formatDueDate = (dateString: string | null, taskStatus?: string) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        const locale = language === 'fr' ? fr : enUS;
        if (isToday(date)) return t('task.today');
        if (isTomorrow(date)) return t('task.tomorrow');
        // Only show overdue if task is not completed
        if (isPast(date) && !isToday(date) && taskStatus !== 'DONE' && taskStatus !== 'CANCELLED') {
            return format(date, 'MMM d', { locale }) + ` (${t('task.overdue')})`;
        }
        if (isThisWeek(date)) return format(date, 'EEEE', { locale });
        return format(date, 'MMM d', { locale });
    };

    const getDueDateColor = (dateString: string | null, taskStatus?: string) => {
        if (!dateString) return 'text-foreground-muted';
        const date = new Date(dateString);
        // Only show red for overdue if task is not completed
        if (isPast(date) && !isToday(date) && taskStatus !== 'DONE' && taskStatus !== 'CANCELLED') return 'text-red-500';
        if (isToday(date)) return 'text-amber-500';
        return 'text-foreground-secondary';
    };

    return (
        <AppLayout>
            {/* Main content */}
            <div className="p-4 sm:p-6 md:p-8">
                <div className="rounded-xl backdrop-blur-md bg-white/5 border border-white/10 p-4 mb-6">
                    <h1 className="text-xl sm:text-2xl font-bold text-white">
                        {t('dashboard.welcome')}, {user?.name?.split(' ')[0]}!
                    </h1>
                </div>

                {/* Quick stats */}
                <div className="mb-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="rounded-xl backdrop-blur-md bg-white/5 border border-white/10 p-4 sm:p-6">
                        <p className="text-sm text-white/70">{t('dashboard.assignedTasks')}</p>
                        <p className="text-2xl sm:text-3xl font-bold text-white">{stats.assigned}</p>
                    </div>
                    <div className="rounded-xl backdrop-blur-md bg-white/5 border border-white/10 p-4 sm:p-6">
                        <p className="text-sm text-white/70">{t('dashboard.dueToday')}</p>
                        <p className="text-2xl sm:text-3xl font-bold text-status-warning">{stats.dueToday}</p>
                    </div>
                    <div className="rounded-xl backdrop-blur-md bg-white/5 border border-white/10 p-4 sm:p-6 sm:col-span-2 lg:col-span-1">
                        <p className="text-sm text-white/70">
                            {t('dashboard.completedThisWeek')}
                        </p>
                        <p className="text-2xl sm:text-3xl font-bold text-status-success">{stats.completedThisWeek}</p>
                    </div>
                </div>

                <div className="mb-8"><PersonalCalendar /></div>

                {/* Recent tasks */}
                <div className="rounded-xl backdrop-blur-md bg-white/5 border border-white/10 p-4 sm:p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-white">
                            {t('nav.myTasks')}
                        </h2>
                        {tasks.length > 0 && (
                            <Link to="/tasks" className="text-sm text-primary hover:underline">
                                {t('common.viewAll')}
                            </Link>
                        )}
                    </div>
                    {tasks.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 sm:py-12 text-center">
                            <CheckSquare className="h-10 w-10 sm:h-12 sm:w-12 text-white/50 mb-4" />
                            <p className="text-white/70 mb-2">{t('task.noTasks')}</p>
                            <p className="text-sm text-white/50 mb-4 px-4">
                                {t('task.noTasksDescription')}
                            </p>
                            <Button onClick={() => setShowCreateDialog(true)}>
                                <Plus className="h-4 w-4 mr-2" />
                                {t('project.createProject')}
                            </Button>
                        </div>
                    ) : (
                        <div className="divide-y divide-white/10 -mx-4 sm:-mx-6">
                            {tasks.slice(0, 5).map((task) => {
                                const status = statusConfig[task.status] || statusConfig.TODO;
                                const StatusIcon = status.icon;

                                return (
                                    <Link
                                        key={task.id}
                                        to={`/projects/${task.projectId}?task=${task.id}`}
                                        className="flex items-center gap-3 sm:gap-4 px-4 sm:px-6 py-3 hover:bg-white/5 transition-colors"
                                    >
                                        <StatusIcon className={cn('h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0', status.color)} />
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-white truncate text-sm sm:text-base">
                                                {task.title}
                                            </p>
                                            <div className="flex items-center gap-1.5 mt-0.5">
                                                {task.project.imageUrl ? (
                                                    <img
                                                        src={task.project.imageUrl}
                                                        alt={task.project.name}
                                                        className="h-4 w-4 rounded object-cover"
                                                    />
                                                ) : (
                                                    <div
                                                        className="h-4 w-4 rounded flex items-center justify-center text-[10px]"
                                                        style={{ backgroundColor: task.project.color || '#6366f1' }}
                                                    >
                                                        {task.project.iconEmoji || '📁'}
                                                    </div>
                                                )}
                                                <span className="text-xs text-white/70 truncate">
                                                    {task.project.name}
                                                </span>
                                            </div>
                                        </div>
                                        {task.dueDate && (
                                            <div className={cn('hidden sm:flex items-center gap-1 text-sm flex-shrink-0', getDueDateColor(task.dueDate, task.status))}>
                                                <Calendar className="h-4 w-4" />
                                                {formatDueDate(task.dueDate, task.status)}
                                            </div>
                                        )}
                                        <ChevronRight className="h-4 w-4 text-white/50 flex-shrink-0" />
                                    </Link>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Create project dialog */}
            <CreateProjectDialog
                open={showCreateDialog}
                onOpenChange={setShowCreateDialog}
            />
        </AppLayout>
    );
}
